import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import useDefaultMarketingFooterPreview from '../hooks/useDefaultMarketingFooterPreview';
import HtmlEditorOverlay from '../MarketingCampaignsForm/DesignForm/HtmlEditorOverlay';
import {
    createMarketingEmailTemplate,
    updateMarketingEmailTemplate,
} from 'services/CampaignsService';
import { getLiteral } from 'utils/getLiteral';
import { errorToast, successToast } from 'utils/toast';

import { MARKETING_TEMPLATES_PATHS } from './marketingTemplatesPaths';
import { useMarketingTemplatesRefresh } from './MarketingTemplatesRefreshContext';

import './TemplateHtmlEditor.scss';

const TemplateHtmlEditor = memo(
    ({ isOpen = false, initialHtml = '', initialTitle = '', templateId, onExit }) => {
        const navigate = useNavigate();
        const { requestMarketingTemplatesRefresh } = useMarketingTemplatesRefresh();
        const footerPreview = useDefaultMarketingFooterPreview(isOpen);

        const isEditMode =
            templateId !== null && templateId !== undefined && String(templateId).length > 0;

        const handleExit = useCallback(
            (discardChanges = true) => {
                onExit?.();
                if (!discardChanges) {
                    navigate(MARKETING_TEMPLATES_PATHS.BASE_LIST);
                }
            },
            [navigate, onExit],
        );

        const handleSave = useCallback(
            ({ html, title }) => {
                const name =
                    typeof title === 'string' && title.trim()
                        ? title.trim()
                        : getLiteral('label_email_template');

                const payload = { name, html };
                const savePromise = isEditMode
                    ? updateMarketingEmailTemplate(templateId, payload)
                    : createMarketingEmailTemplate(payload);

                savePromise
                    .then(() => {
                        successToast({
                            title: getLiteral(
                                isEditMode
                                    ? 'title_success_template_updated'
                                    : 'title_success_template_created',
                            ),
                            text: getLiteral(
                                isEditMode
                                    ? 'label_success_template_updated'
                                    : 'label_success_template_created',
                            ),
                        });
                        if (isEditMode) {
                            requestMarketingTemplatesRefresh();
                        }
                        navigate(MARKETING_TEMPLATES_PATHS.BASE_LIST);
                        onExit?.();
                    })
                    .catch(() => {
                        errorToast({
                            title: getLiteral(
                                isEditMode
                                    ? 'title_error_template_updated'
                                    : 'title_error_template_created',
                            ),
                            text: getLiteral(
                                isEditMode
                                    ? 'label_error_template_updated'
                                    : 'label_error_template_created',
                            ),
                        });
                    });
            },
            [navigate, onExit, isEditMode, requestMarketingTemplatesRefresh, templateId],
        );

        if (!isOpen) {
            return null;
        }

        return (
            <div className="marketing-templates-html-editor">
                <HtmlEditorOverlay
                    initialHtml={initialHtml}
                    initialTitle={initialTitle}
                    isOpen={isOpen}
                    editableTitle={true}
                    footerPreview={footerPreview}
                    onSave={handleSave}
                    onExitWithoutSaving={handleExit}
                />
            </div>
        );
    },
);

TemplateHtmlEditor.displayName = 'TemplateHtmlEditor';

TemplateHtmlEditor.propTypes = {
    isOpen: PropTypes.bool,
    initialHtml: PropTypes.string,
    initialTitle: PropTypes.string,
    templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onExit: PropTypes.func,
};

export default TemplateHtmlEditor;
